import type {Session} from '../types/session'

const NANO_CREDITS_PER_TENTH = 100_000_000

/**
 * Spend recorded against a single session, in raw billionths of an AI credit.
 * The API reports this as `amount`; `credits` is a deprecated alias carrying the
 * same value, so it is only read when `amount` is absent.
 */
export function getSessionSpendNanoAiu(session: Pick<Session, 'usage'>): number | undefined {
  if (session.usage?.type !== 'ai_credits') return undefined
  return session.usage.amount ?? session.usage.credits
}

export function sumSessionSpendNanoAiu(sessions: Array<Pick<Session, 'usage'>>): number | undefined {
  return sessions.reduce<number | undefined>((total, session) => {
    const spend = getSessionSpendNanoAiu(session)
    return spend === undefined ? total : (total ?? 0) + spend
  }, undefined)
}

export function formatAiCredits(nanoCredits: number): number {
  return Math.round((nanoCredits / 1_000_000_000) * 10) / 10
}

export function reconcileSessionAiCredits(sessions: Array<Pick<Session, 'id' | 'usage'>>): ReadonlyMap<string, number> {
  const creditSessions = sessions
    .map((session, index) => ({
      id: session.id,
      index,
      nanoCredits: getSessionSpendNanoAiu(session) ?? 0,
    }))
    .filter(session => session.nanoCredits > 0)
  const totalTenths = Math.round(
    creditSessions.reduce((total, session) => total + session.nanoCredits, 0) / NANO_CREDITS_PER_TENTH,
  )
  const allocations = creditSessions.map(session => ({
    ...session,
    tenths: Math.floor(session.nanoCredits / NANO_CREDITS_PER_TENTH),
    remainder: session.nanoCredits % NANO_CREDITS_PER_TENTH,
  }))
  let remainingTenths = totalTenths - allocations.reduce((total, session) => total + session.tenths, 0)

  for (const session of [...allocations].sort((a, b) => b.remainder - a.remainder || a.index - b.index)) {
    if (remainingTenths === 0) break
    session.tenths += 1
    remainingTenths -= 1
  }

  return new Map(allocations.map(session => [session.id, session.tenths / 10]))
}

export type SessionFooterUsage = {type: 'ai-credits'; amount: number} | {type: 'premium-requests'; amount: number}

export function getSessionFooterUsage(
  session: Pick<Session, 'premium_requests' | 'usage'>,
  reconciledAiCredits?: number,
): SessionFooterUsage | undefined {
  const rawAiCredits = getSessionSpendNanoAiu(session) ?? 0
  const aiCredits = reconciledAiCredits ?? formatAiCredits(rawAiCredits)

  if (session.usage?.type === 'pru') {
    return session.premium_requests > 0 ? {type: 'premium-requests', amount: session.premium_requests} : undefined
  }

  return session.usage?.type === 'ai_credits' && rawAiCredits > 0 ? {type: 'ai-credits', amount: aiCredits} : undefined
}
